import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Edit,
  Delete,
  Close,
  Visibility,
  Print,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ResultCount from "../components/ResultCount";
import ClearFiltersButton from "../components/ClearFiltersButton";
import TemplatePreview from "../components/TemplatePreview";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../hooks/useToast";
import { Toast } from "../components/Toast";
import ConfirmModal from "../components/modals/ConfirmModal";
import RichTextEditor from "../components/RichTextEditor";
import SavingOverlay from "../components/SavingOverlay";
import {
  printTemplateService,
  PrintTemplate,
} from "../services/printTemplateService";
import { colors, typography, pageHeader } from "../theme/designSystem";

const MAX_LOGO_BYTES = 700 * 1024; // 700 KB

interface TemplateForm {
  id?: string;
  title: string;
  file: string;
  header: string;
  footer: string;
}

const emptyForm: TemplateForm = { title: "", file: "", header: "", footer: "" };

const EntitySettings: React.FC = () => {
  const { navigateTo, getAllParams } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const params = getAllParams();
  const entityId = params.get("entityId") || user?.entityId || "";
  const entityName = params.get("entityName") || user?.entityName || "Entidade";

  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"todos" | "ativos" | "inativos">("todos");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<TemplateForm>(emptyForm);
  const [logoError, setLogoError] = useState("");
  const [titleError, setTitleError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<PrintTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<PrintTemplate | null>(
    null
  );

  const loadTemplates = async () => {
    if (!entityId) return;
    try {
      const data = await printTemplateService.getByEntity(entityId);
      setTemplates(data);
    } catch (error) {
      console.error("Erro ao carregar templates:", error);
      showToast("Erro ao carregar os templates.", "error");
    }
  };

  /** Minusculas e sem acento: quem busca "padrao" espera achar "Padrão". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = templates.filter((t) => {
    if (status !== "todos" && t.status !== (status === "ativos")) {
      return false;
    }
    return !search.trim() || normalize(t.title || "").includes(normalize(search));
  });

  const clearFilters = () => {
    setSearch("");
    setStatus("todos");
  };

  const hasFilterChanges = search !== "" || status !== "todos";

  useEffect(() => {
    document.title = `${entityName} / Templates`;
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setLogoError("");
    setTitleError("");
    setIsModalOpen(true);
  };

  const openEdit = (t: PrintTemplate) => {
    setForm({
      id: t.id,
      title: t.title || "",
      file: t.file || "",
      header: t.header || "",
      footer: t.footer || "",
    });
    setLogoError("");
    setTitleError("");
    setIsModalOpen(true);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("A logo deve ter no máximo 700 KB.");
      return;
    }
    setLogoError("");
    const reader = new FileReader();
    reader.onload = () =>
      setForm((f) => ({ ...f, file: reader.result as string }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (saving) return; // evita duplo clique durante o processamento
    if (!form.title.trim()) {
      setTitleError("Título obrigatório");
      return;
    }
    const payload = {
      title: form.title.trim(),
      file: form.file,
      header: form.header,
      footer: form.footer,
    };
    setSaving(true);
    try {
      if (form.id) {
        await printTemplateService.update(form.id, payload);
        showToast("Template atualizado com sucesso", "success");
      } else {
        await printTemplateService.create(entityId, payload);
        showToast("Template criado com sucesso", "success");
      }
      setIsModalOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      showToast("Erro ao salvar o template. Tente novamente.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (t: PrintTemplate) => {
    if (t.isDefault) return; // o template padrão não pode ser inativado
    try {
      await printTemplateService.setStatus(t.id, !t.status);
      showToast(
        !t.status ? "Template ativado" : "Template inativado",
        "success"
      );
      await loadTemplates();
    } catch (error) {
      console.error("Erro ao alterar status do template:", error);
      showToast("Erro ao alterar o status do template.", "error");
    }
  };

  const handleSetDefault = async (t: PrintTemplate) => {
    if (t.isDefault) return;
    try {
      await printTemplateService.setDefault(t.id);
      showToast("Template definido como padrão", "success");
      await loadTemplates();
    } catch (error) {
      console.error("Erro ao definir template padrão:", error);
      showToast("Erro ao definir o template como padrão.", "error");
    }
  };

  const handleConfirmDelete = async () => {
    if (!toDelete) return;
    try {
      await printTemplateService.remove(toDelete.id);
      showToast("Template excluído com sucesso", "success");
      setToDelete(null);
      await loadTemplates();
    } catch (error) {
      console.error("Erro ao excluir template:", error);
      showToast("Erro ao excluir o template.", "error");
    }
  };

  // Abre uma janela limpa com o A4 (logo/cabeçalho/rodapé) e dispara a impressão.
  const handlePrint = () => {
    if (!previewTemplate) return;
    const { file, header, footer, title } = previewTemplate;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return;
    w.document.write(
      `<!DOCTYPE html><html><head><title>${title || "Template"}</title>` +
        `<style>` +
        `@page { size: A4; margin: 0; }` +
        `body { margin: 0; font-family: Arial, sans-serif; }` +
        `.a4 { width: 210mm; min-height: 297mm; padding: 15mm; box-sizing: border-box; position: relative; font-size: 12pt; color: #000; }` +
        `.top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10mm; }` +
        `.logo img { max-height: 30mm; max-width: 70mm; object-fit: contain; }` +
        `.header { text-align: right; flex: 1; }` +
        `.footer { position: absolute; left: 15mm; right: 15mm; bottom: 8mm; text-align: center; }` +
        // Conteudo do Quill: igualar o espacamento/alinhamento/fontes da visualizacao.
        `.a4 p { margin: 0; }` +
        `.a4 ol, .a4 ul { margin: 0; padding-left: 1.5em; }` +
        `.ql-align-center { text-align: center; }` +
        `.ql-align-right { text-align: right; }` +
        `.ql-align-justify { text-align: justify; }` +
        `.ql-font-arial { font-family: Arial, sans-serif; }` +
        `.ql-font-times-new-roman { font-family: "Times New Roman", serif; }` +
        `.ql-font-courier-new { font-family: "Courier New", monospace; }` +
        `.ql-font-georgia { font-family: Georgia, serif; }` +
        `.ql-font-verdana { font-family: Verdana, sans-serif; }` +
        `.ql-font-tahoma { font-family: Tahoma, sans-serif; }` +
        `</style></head><body>` +
        `<div class="a4">` +
        `<div class="top"><div class="logo">${
          file ? `<img src="${file}" />` : ""
        }</div>` +
        `<div class="header">${header || ""}</div></div>` +
        `<div class="footer">${footer || ""}</div>` +
        `</div></body></html>`
    );
    w.document.close();
    w.focus();
    // Aguarda a logo (base64) renderizar antes de imprimir.
    setTimeout(() => w.print(), 300);
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          {/* Título da Página */}
          <div className="c4u-page-header c4u-page-header--in-panel">
            <div className="c4u-page-header__texts">
              <h1 className="c4u-page-header__title">{entityName} / Templates</h1>
              <p className="c4u-page-header__subtitle">Templates de documentos (logo, cabeçalho e rodapé) usados na impressão de relatórios, receituários, etc.</p>
            </div>
            <div className="c4u-page-header__actions">
              <FaqButton />
            </div>
          </div>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por título"
                  placeholder="Buscar template"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as "todos" | "ativos" | "inativos")}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="ativos">Ativos</MenuItem>
                  <MenuItem value="inativos">Inativos</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Adicionar template" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={templates.length} />

          <Box className="admin-plans-list-container" sx={{ mt: 1, background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
            <Box className="admin-plans-table admin-entities-table">
              <Box className="c4u-table__header">
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 80px", textAlign: "left" }}
                >
                  Logo
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "1 1 auto", textAlign: "left" }}
                >
                  Título
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 150px", textAlign: "left" }}
                >
                  Padrão
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 110px", textAlign: "left" }}
                >
                  Status
                </Box>
                <Box
                  className="admin-plans-header-cell"
                  sx={{ flex: "0 0 140px", justifyContent: "flex-end" }}
                >
                  Ações
                </Box>
              </Box>

              <Box className="admin-plans-table-body">
                {filtered.map((t) => (
                    <Box
                      key={t.id}
                      className="c4u-table__row"
                    >
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 80px", textAlign: "left" }}
                        data-label="Logo"
                      >
                        {t.file ? (
                          <img
                            src={t.file}
                            alt="logo"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "contain",
                              borderRadius: 4,
                              border: "1px solid #e9ecef",
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "4px",
                              border: "1px dashed #e9ecef",
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{
                          flex: "1 1 auto",
                          textAlign: "left",
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                        data-label="Título"
                      >
                        {t.title}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 150px", textAlign: "left" }}
                        data-label="Padrão"
                      >
                        {t.isDefault ? (
                          <Chip
                            label="Padrão"
                            size="small"
                            sx={{ color: "white", backgroundColor: "#03B4C6" }}
                          />
                        ) : (
                          <Chip
                            label="Tornar padrão"
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetDefault(t);
                            }}
                            sx={{
                              cursor: "pointer",
                              borderColor: "#03B4C6",
                              color: "#03B4C6",
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        className="admin-plans-cell"
                        sx={{ flex: "0 0 110px", textAlign: "left" }}
                        data-label="Status"
                      >
                        <Chip
                          label={t.status ? "Ativo" : "Inativo"}
                          size="small"
                          onClick={
                            t.isDefault
                              ? undefined
                              : (e) => {
                                  e.stopPropagation();
                                  handleToggleStatus(t);
                                }
                          }
                          disabled={t.isDefault}
                          title={
                            t.isDefault
                              ? "Template padrão não pode ser inativado"
                              : t.status
                              ? "Clique para inativar"
                              : "Clique para ativar"
                          }
                          sx={{
                            color: "white",
                            fontWeight: 600,
                            cursor: t.isDefault ? "default" : "pointer",
                            backgroundColor: t.status ? "#28a745" : "#adb5bd",
                            "&.Mui-disabled": {
                              opacity: 1,
                              color: "white",
                              backgroundColor: t.status ? "#28a745" : "#adb5bd",
                            },
                          }}
                        />
                      </Box>
                      <Box
                        className="admin-plans-cell admin-plans-actions"
                        sx={{ flex: "0 0 140px", textAlign: "right" }}
                        data-label="Ações"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewTemplate(t);
                            }}
                            title="Visualizar (A4)"
                            sx={{
                              backgroundColor: "#6c757d",
                              color: "white",
                              "&:hover": { backgroundColor: "#5a6268" },
                            }}
                          >
                            <Visibility sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={() => openEdit(t)}
                            title="Editar"
                            sx={{
                              backgroundColor: "#03B4C6",
                              color: "white",
                              "&:hover": { backgroundColor: "#029AAB" },
                            }}
                          >
                            <Edit sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton
                            className="c4u-row-action"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setToDelete(t);
                            }}
                            disabled={t.isDefault}
                            title={
                              t.isDefault
                                ? "Template padrão não pode ser excluído"
                                : "Excluir"
                            }
                            sx={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              "&:hover": { backgroundColor: "#c82333" },
                              "&.Mui-disabled": {
                                backgroundColor: "#e9a3aa",
                                color: "white",
                              },
                            }}
                          >
                            <Delete sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                ))}
              </Box>
            </Box>
          </Box>
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      <Dialog
        open={isModalOpen}
        onClose={(_, reason) => {
          // bloqueia fechar (ESC / clique fora) enquanto processa
          if (saving) return;
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setIsModalOpen(false);
            return;
          }
          setIsModalOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "92vh",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {form.id ? "Editar Template" : "Novo Template"}
          <IconButton
            onClick={() => setIsModalOpen(false)}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <TextField
            label="Título"
            value={form.title}
            onChange={(e) => {
              setForm((f) => ({ ...f, title: e.target.value }));
              if (titleError) setTitleError("");
            }}
            error={!!titleError}
            helperText={titleError}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Logo
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            {form.file ? (
              <img
                src={form.file}
                alt="logo"
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "contain",
                  border: "1px solid #e9ecef",
                  borderRadius: 4,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  border: "1px dashed #ced4da",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.textSecondary,
                  fontSize: 12,
                }}
              >
                sem logo
              </Box>
            )}
            <Button variant="outlined" component="label" sx={{ textTransform: "none" }}>
              Selecionar logo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleLogoChange}
              />
            </Button>
            {form.file && (
              <Button
                variant="text"
                color="error"
                onClick={() => setForm((f) => ({ ...f, file: "" }))}
                sx={{ textTransform: "none" }}
              >
                Remover
              </Button>
            )}
          </Box>
          {logoError && (
            <Typography color="error" variant="caption" sx={{ display: "block", mb: 1 }}>
              {logoError}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: colors.textSecondary, display: "block", mb: 2 }}>
            Imagem até 700 KB (PNG/JPG).
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Cabeçalho
          </Typography>
          <Box sx={{ mb: 2 }}>
            <RichTextEditor
              key={`header-${form.id ?? "new"}`}
              value={form.header}
              onChange={(html) => setForm((f) => ({ ...f, header: html }))}
              placeholder="Conteúdo do cabeçalho..."
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Rodapé
          </Typography>
          <Box>
            <RichTextEditor
              key={`footer-${form.id ?? "new"}`}
              value={form.footer}
              onChange={(html) => setForm((f) => ({ ...f, footer: html }))}
              placeholder="Conteúdo do rodapé..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "1rem 1.5rem", gap: 1 }}>
          <Button
            onClick={() => setIsModalOpen(false)}
            disabled={saving}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "white" }} />
              ) : undefined
            }
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              "&.Mui-disabled": {
                backgroundColor: colors.primary,
                opacity: 0.7,
                color: "white",
              },
            }}
          >
            {saving ? "Processando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!toDelete}
        title="Excluir Template"
        message={
          toDelete
            ? `Tem certeza que deseja excluir o template "${toDelete.title}"?`
            : ""
        }
        warningMessage="Esta ação não poderá ser desfeita."
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={() => setToDelete(null)}
        type="danger"
      />

      <Dialog
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", maxHeight: "95vh" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
          }}
        >
          Visualização — {previewTemplate?.title}
          <IconButton
            onClick={() => setPreviewTemplate(null)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ background: "#e9ecef", p: 2, overflow: "auto" }}>
          {previewTemplate && (
            <TemplatePreview
              file={previewTemplate.file}
              header={previewTemplate.header}
              footer={previewTemplate.footer}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: "0.75rem 1.5rem", gap: 1 }}>
          <Button
            onClick={() => setPreviewTemplate(null)}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Fechar
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<Print />}
            sx={{ backgroundColor: colors.primary, textTransform: "none" }}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default EntitySettings;
